// The form field: structure ported from libms components/ui/Field.tsx
// (`flex flex-col gap-1.5` — label, control, hint UNDER it), inked to
// Spectrum's text-field anatomy (2026-09-02). The Spectrum rule: label and
// help text share ONE size and ONE ink — font-size-75 = 12px in gray-700 —
// where the port had an 11px/45% label over a 10px/35% hint (2.9:1 and
// 2.2:1, both failing WCAG AA 4.5:1 at those sizes). Controls stay the
// source's compact `-sm` variants at 13px with 30%-ink placeholders. mini
// keeps two additions the source lacks: an `error` slot (which replaces the
// hint) and the aria-label context below, since mini's controls are
// id-addressed by tests.
import {
  type Component,
  type JSX,
  Show,
  children,
  createContext,
  createEffect,
  createUniqueId,
  splitProps,
  useContext,
} from "solid-js";
import { Dynamic } from "solid-js/web";
import { CircleAlert } from "lucide-solid";

// The fieldset-legend names the GROUP, not the control — so Field shares its
// label through context and the kit controls below adopt it as their
// aria-label fallback. One association mechanism for every form in the app;
// an explicit aria-label on a control still wins.
//
// THE SAME CONTEXT CARRIES THE VALIDATION STATE (2026-09-18, audit UX-05).
// Field renders the error text, but the CONTROL is what a screen reader has
// focused — so the control must say "invalid" and point at the message.
// Field publishes `invalid` and the id of whichever help row is showing
// (`${id}-error`, else `${id}-hint`), and every kit control inside adopts
// them as `aria-invalid` / `aria-describedby`. The negative border rides the
// attribute: tokens.css paints `[aria-invalid="true"]` in the error stop for
// every `.input` / `.select` / `.textarea`, so a hand-rolled control that sets
// the attribute gets the same border as a kit one (DEV-GUIDE-002 §12.4 "Form
// validation — negative-900 border + text-error message; the keyboard ring
// stays blue"). Before this, the audit measured `aria-invalid` at 0 hits
// across every form and the border stayed grey on a failed submit.
interface FieldContextValue {
  label: string;
  /** True while Field shows an `error`. */
  invalid: () => boolean;
  /** The id of the help row on screen — the error when there is one, else the hint, else nothing. */
  describedBy: () => string | undefined;
}
const FieldContext = createContext<FieldContextValue | undefined>(undefined);

/** The three ARIA attributes a control inside a Field carries; explicit props win over the context. */
export interface FieldControlAria {
  "aria-label"?: string;
  "aria-invalid"?: JSX.HTMLAttributes<HTMLElement>["aria-invalid"];
  "aria-describedby"?: string;
}

/**
 * Adopt the enclosing Field's label, validity and help-text id — for a kit
 * control that renders its own `<input>` (MoneyInput, a searchable select).
 * Returns accessors so the attributes stay reactive when the error appears.
 * Outside a Field it returns the explicit values unchanged.
 */
export function useFieldControl(explicit: FieldControlAria = {}) {
  const ctx = useContext(FieldContext);
  return {
    label: () => explicit["aria-label"] ?? ctx?.label,
    invalid: () => explicit["aria-invalid"] ?? (ctx?.invalid() ? ("true" as const) : undefined),
    describedBy: () => explicit["aria-describedby"] ?? ctx?.describedBy(),
  };
}

/** Shared field classes (daisyUI 5 — border is default, no *-bordered).
 * ONE focus mechanism: daisyUI derives both the resting border and the focus
 * outline from `--input-color`, which it declares on the component itself — so
 * a theme-level value can never reach it and `focus:border-primary` only ever
 * painted a 1px blue border *inside* daisyUI's 2px near-black outline (visible
 * on native selects, which uniquely match `:open`). These arbitrary-property
 * utilities retarget that one variable at the tokens defined in
 * @erp/config/tailwind/base.css; `focus:border-primary` is gone.
 *
 * `min-w-0` is load-bearing for mobile: `.fieldset` is a GRID and its implicit
 * column is sized to the control's min-content, while a form control defaults
 * to `min-width:auto` — so the track froze at the control's intrinsic ~400px
 * and every form scrolled sideways on a phone.
 *
 * FOCUS is a two-part treatment, and both parts live in @erp/ui/tokens.css:
 * POINTER focus turns the 1px border brand-coloured (the `--input-color`
 * retarget below is what the border reads), and KEYBOARD focus adds Spectrum's
 * 2px ring at a 2px gap in the one ring colour the whole kit shares. The
 * keyboard half is gated on @erp/ui/focus-modality's `data-ui-modality`
 * attribute (installed by AppShell): browsers match `:focus-visible` on text
 * fields even for a mouse click, which drew border AND ring at once — the
 * "double blue border" defect, fixed 2026-09-02. daisyUI's own ring fires on
 * every click, in a per-variant colour — tokens.css suppresses it on `:focus`.
 * These constants carry NO outline utility of their own: the
 * `focus:outline-none focus-within:outline-none` an earlier version had was
 * written to kill daisyUI's click ring, which tokens.css already does for
 * every `.input` in the product — and a per-element outline-none is exactly
 * the kind of utility that hides the keyboard ring from the next reader. */
// text-sm is 14px = font-size-100, which is Adobe's own size for a text field
// at component-height-100 — and these render at exactly 32px (daisyUI's
// input-sm is --size-field x8, and --size-field is 0.25rem at desktop scale).
// The 13px this replaced was on nobody's ramp: Spectrum has no 13 at desktop
// scale and daisyUI's own floor is 12.
export const inputClass =
  "input input-sm w-full min-w-0 bg-base-100 text-sm placeholder:text-base-subtle [--input-color:var(--input-border-color)] focus:[--input-color:var(--input-border-color-focus)] focus-within:[--input-color:var(--input-border-color-focus)]";
export const selectClass =
  "select select-sm w-full min-w-0 bg-base-100 text-sm [--input-color:var(--input-border-color)] focus:[--input-color:var(--input-border-color-focus)] focus-within:[--input-color:var(--input-border-color-focus)] open:[--input-color:var(--input-border-color-focus)]";
export const textareaClass =
  "textarea w-full min-w-0 bg-base-100 text-sm leading-relaxed placeholder:text-base-subtle [--input-color:var(--input-border-color)] focus:[--input-color:var(--input-border-color-focus)] focus-within:[--input-color:var(--input-border-color-focus)]";

export interface FieldProps {
  label: string;
  /**
   * A lucide-solid component rendered before the label text — every field is
   * meant to carry one, so the eye finds the right control by shape before it
   * reads a word. Passed as the COMPONENT (`icon={Mail}`), not an element, so
   * Field owns the 12px sizing and the muted tone.
   */
  icon?: Component<{ size?: number; class?: string }>;
  /** Reference text under the control (e.g. "MARC 245 $a") — ALWAYS rendered,
   * as in the source; an `error` replaces it. */
  hint?: string;
  /** Validation message — always rendered and wins over the hint. */
  error?: string;
  required?: boolean;
  class?: string;
  /**
   * Automation id on the wrapper; the help rows derive `${id}-hint` /
   * `${id}-error` from it and the control's aria-describedby points at
   * whichever is showing. Without one a unique id is minted, so the wiring
   * never depends on the caller.
   */
  id?: string;
  children: JSX.Element;
}

export function Field(props: FieldProps) {
  const uid = createUniqueId();
  const baseId = () => props.id ?? `field-${uid}`;
  const errorId = () => `${baseId()}-error`;
  const hintId = () => `${baseId()}-hint`;
  const ctx: FieldContextValue = {
    get label() {
      return props.label;
    },
    invalid: () => Boolean(props.error),
    describedBy: () => (props.error ? errorId() : props.hint ? hintId() : undefined),
  };
  return (
    <FieldContext.Provider value={ctx}>
      {/* Structure ported from libms components/ui/Field.tsx: a flex column
          at gap-1.5 — label, control, then the hint UNDER the
          control. Not daisyUI's fieldset/legend, whose legend sits inside the
          control's border box and whose default rhythm is far looser than the
          source's. `min-w-0` stays: as a grid child this must be allowed to
          shrink below its control's preferred width, or a phone scrolls
          sideways. */}
      <div id={props.id} class={`flex min-w-0 flex-col gap-1.5 ${props.class ?? ""}`}>
        {/* Spectrum field label: sentence case at font-size-75 — 12px, the
            SAME size as the help text below — in gray-700 ink; the uppercase
            tracking-wide eyebrow was the port's, not Spectrum's. `/84` IS
            gray-700: base-content is gray-800 #222222, and 0.84·34 + 0.16·255
            = rgb(69,69,69) ≈ --spectrum-gray-700 rgb(70,70,70), 9.4:1 on
            white; the alpha rides the theme-scoped base-content, so dark
            composites onto the dark ramp with no per-theme override. Weight
            500 where Spectrum ships regular — the one deliberate half-step,
            so a 12px label still reads as chrome beside 13px control text. */}
        <label class="flex items-center gap-1.5 text-xs font-medium text-base-muted">
          <Show when={props.icon}>
            <span class="flex shrink-0">
              <Dynamic component={props.icon} size={12} aria-hidden="true" />
            </span>
          </Show>
          {props.label}
          <Show when={props.required}>
            {/* Spectrum's required asterisk INHERITS the label ink — it is
                not a negative-colour mark. The offset stays Spectrum's:
                gap-1.5 (6px) − ml-0.5 (2px) = 4px =
                --spectrum-field-label-text-to-asterisk-medium. */}
            <span class="-ml-0.5" aria-hidden="true">
              *
            </span>
          </Show>
        </label>
        {props.children}
        {/* The hint is ALWAYS rendered (the source's behaviour): "MARC 245 $a"
            under the control is reference material a cataloguer reads while
            typing, not optional chrome. helpMode still gates the bigger
            HelpNote panels. An error replaces it. Hint and error are BOTH
            Spectrum help text: the label's own font-size-75/gray-700, never a
            step smaller. */}
        <Show
          when={props.error}
          fallback={
            <Show when={props.hint}>
              <span id={hintId()} class="text-xs leading-snug text-base-muted">
                {props.hint}
              </span>
            </Show>
          }
        >
          {/* Negative help text, Spectrum anatomy: an alert icon leads the
              message at a 7px gap (--spectrum-text-to-visual-75) with a 3px
              top offset (--spectrum-help-text-top-to-workflow-icon-medium).
              16px, not the 18px --spectrum-workflow-icon-size-100 — Spectrum
              sizes that for its 24px-min-height help row, which would swell
              every errored field in this dense console; 16px holds this row
              and matches the kit's in-field icon. Ink is text-error = the
              theme's negative-color-900 stop (#d31510 light / #ff816b darkest),
              already Spectrum-valued in base.css. NO role="alert" here: the
              control announces the message through aria-describedby when it
              takes focus, and the FORM's summary (FormPage `errors`) is the one
              live region — a role on every field would fire a dozen assertive
              announcements at once on a failed submit. */}
          <span id={errorId()} class="flex items-start gap-[7px] text-xs leading-snug text-error">
            <CircleAlert size={16} class="mt-[3px] shrink-0" aria-hidden="true" />
            {props.error}
          </span>
        </Show>
      </div>
    </FieldContext.Provider>
  );
}

const ARIA_KEYS = ["class", "aria-label", "aria-invalid", "aria-describedby"] as const;

export function TextInput(props: JSX.InputHTMLAttributes<HTMLInputElement>) {
  const [local, rest] = splitProps(props, ARIA_KEYS);
  const aria = useFieldControl(local);
  return (
    <input
      aria-label={aria.label()}
      aria-invalid={aria.invalid()}
      aria-describedby={aria.describedBy()}
      class={`${inputClass} ${local.class ?? ""}`}
      {...rest}
    />
  );
}

export function Select(props: JSX.SelectHTMLAttributes<HTMLSelectElement>) {
  const [local, rest] = splitProps(props, [...ARIA_KEYS, "children", "value"]);
  const aria = useFieldControl(local);
  // A <select> only accepts a value once its <option>s are in the DOM. On a
  // RAW <select> Solid compiles `value={sig()}` into a deferred effect, so the
  // options already exist by the time it lands; routed through a component the
  // same prop arrives in the element's initial `spread`, while it is still
  // empty — the browser drops it and falls back to option 0. Every edit form
  // that opened with a stored choice therefore showed the FIRST option instead.
  // `value` is split out of the spread and re-applied here: after mount, and
  // again whenever the value or the option list (async-loaded lists) changes.
  const options = children(() => local.children);
  let el!: HTMLSelectElement;
  createEffect(() => {
    options();
    const v = local.value;
    if (v == null || Array.isArray(v)) return; // uncontrolled, or a multi-select
    const next = String(v);
    if (el.value !== next) el.value = next;
  });
  return (
    <select
      ref={el}
      aria-label={aria.label()}
      aria-invalid={aria.invalid()}
      aria-describedby={aria.describedBy()}
      class={`${selectClass} ${local.class ?? ""}`}
      {...rest}
    >
      {options()}
    </select>
  );
}

export function Textarea(props: JSX.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const [local, rest] = splitProps(props, ARIA_KEYS);
  const aria = useFieldControl(local);
  return (
    <textarea
      aria-label={aria.label()}
      aria-invalid={aria.invalid()}
      aria-describedby={aria.describedBy()}
      class={`${textareaClass} ${local.class ?? ""}`}
      {...rest}
    />
  );
}
